<template>
  <a-modal
    :open="visible"
    :destroy-on-close="true"
    title="Генерировать результат"
    ok-text="Подтвердить"
    cancel-text="Отмена"
    centered
    :confirm-loading="isSubmitting"
    @ok="onModalSubmit"
    @cancel="closeModal">
    <template v-if="isNewPlanOptionsVisible">
      <a-row
        :gutter="[16, 16]"
        :style="{ margin: '1rem 0' }">
        <template
          v-for="checkbox in checkboxes"
          :key="checkbox.field">
          <a-col :style="{ paddingLeft: '0', paddingRight: '0.5rem' }">
            <a-checkbox
              v-model:checked="modal[checkbox.field]">
              {{ checkbox.label }}
            </a-checkbox>
          </a-col>
        </template>
      </a-row>
    </template>

    <label>Качество:</label>
    <a-select
      v-model:value="modal.unrealRtxPreset"
      :style="{ width: '100%', marginBottom: '1rem' }">
      <a-select-option
        v-for="(value, label) in RtxPreset"
        :key="value"
        :value="value">
        {{ label }}
      </a-select-option>
    </a-select>

    <plan-selects
      v-model:user-has-permission-for-new-options="userHasPermissionForNewOptions"
      v-model:user-is-admin="userIsAdmin"
      v-model:projection-mode="modal.projectionMode"
      v-model:camera-navigation="modal.cameraNavigation"
      v-model:unreal-top-down-view-count="modal.unrealTopDownViewCount"
      v-model:lossless-format="modal.losslessFormat"
      v-model:panoramas-type="modal.panoramasType"
      v-model:unreal-middle-cut-height="modal.unrealMiddleCutHeight"
      v-model:camera-iso="modal.cameraIso"
      v-model:renderServer="modal.renderServer"
      :filtered-camera-navigation-options="filteredCameraNavigationOptions"
      :is-camera-navigation-select-visible="isCameraNavigationSelectVisible"
      :is-unreal-top-down-view-count-visible="isUnrealTopDownCountVisible"
      :is-png-lossless-format-choice-visible="isPngLosslessFormatChoiceVisible"
      :is-panoramas-type-choice-visible="isPanoramasTypeChoiceVisible"
      :is-middle-cut-height-choice-visible="isMiddleCutHeightChoiceVisible"
      :is-camera-iso-choice-visible="isCameraIsoChoiceVisible" />
  </a-modal>
</template>

<script setup>

import { reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import PlanSelects from '@/Components/CRM/Upload/PlanSelects.vue';
import getFlag from '@/helpers/getFlag';
import { PanoramasType } from '@/enums/PanoramasType';
import { TopViewsProjectionMode } from '@/enums/TopViewsProjectionMode';
import { CameraNavigationMode } from '@/enums/CameraNavigationMode';
import { LosslessFormat } from '@/enums/losslessFormatOptions';
import { useCameraNavigationOptions } from '@/composables/useCameraNavigationOptions';
import { RtxPreset } from '@/enums/RtxPreset';
import { api } from '@/api';
import { launchWithConfirm } from '@/helpers/launchWithConfirm';

const props = defineProps({
  plan: {
    type: Object,
    default: () => ({}),
  },
  visible: {
    default: () => false,
    type: Boolean,
  },
});

const getDefaultModalData = () => ({
  steps: ['unreal'],
  isUnrealPanoramasCaptureEnabled: props.plan.plan.options?.is_unreal_panoramas_capture_enabled ?? false,
  isRulerEnabled: props.plan.capabilities?.ruler?.enabled ?? false,
  isInteractiveTopViewEnabled: props.plan.capabilities?.interactive_top_view?.enabled ?? false,
  isTopViewAsMiniPlanEnabled: props.plan.capabilities?.top_view_as_miniplan?.enabled ?? false,
  hideOptionalFurniture: props.plan.capabilities?.hidden_furniture?.enabled ?? false,
  isTransparentTopViewEnabled: props.plan.plan.options?.middle_cut_render_modes?.color_masked ?? false,
  isWebpEnabled: props.plan.plan.options?.is_webp_enabled ?? false,
  unrealRtxPreset: props.plan.plan.options?.unreal_rtx_preset ?? RtxPreset.Medium,
  projectionMode: props.plan.plan.options?.projection_mode ?? TopViewsProjectionMode.Perspective,
  renderServer: props.plan.plan.options?.override_node_label ?? null,
  cameraNavigation: props.plan.capabilities?.camera_navigation ?? CameraNavigationMode.Classic,
  unrealTopDownViewCount: props.plan.plan.options?.unreal_top_down_view_count ?? 12,
  losslessFormat: props.plan.plan.options?.lossless_format ?? LosslessFormat.Png,
  panoramasType: props.plan.plan.options?.panoramas_type ?? PanoramasType.Sphere,
  unrealMiddleCutHeight: props.plan.plan.options?.unreal_middle_cut_height ?? 150,
  applyMiddleCutInTopView: props.plan.plan.options?.apply_middle_cut_in_top_view
    ?? props.plan.plan.options?.applyMiddleCutInTopView
    ?? false,
  cameraIso: props.plan.plan.options?.camera_iso ?? 500,
});

const modal = reactive({});
const isSubmitting = ref(false);

const {
  filteredOptions: filteredCameraNavigationOptions,
  isSelectVisible: isCameraNavigationSelectVisible,
} = useCameraNavigationOptions();

const emit = defineEmits(['close']);
const store = useStore();

const userHasPermissionForNewOptions = ref(store.getters['auth/hasPermission'](
  'ui.action.pages_crm_plans_modals_startgenerationmodal.1457',
));
const userIsAdmin = ref(store.getters['auth/hasPermission']('ui.action.pages_crm_plans_modals_startgenerationmodal.1'));

const isNewPlanOptionsVisible = ref(getFlag('isNewPlanOptionsVisible'));
const isUnrealTopDownCountVisible = ref(getFlag('isUnrealTopDownCountVisible'));
const isPngLosslessFormatChoiceVisible = ref(getFlag('isPngLosslessFormatChoiceVisible'));
const isPanoramasTypeChoiceVisible = ref(getFlag('isPanoramasTypeChoiceVisible'));
const isMiddleCutHeightChoiceVisible = ref(getFlag('isMiddleCutHeightChoiceVisible'));
const isCameraIsoChoiceVisible = ref(getFlag('isCameraIsoChoiceVisible'));

const checkboxes = [
  { field: 'isUnrealPanoramasCaptureEnabled', label: 'Включить панорамы' },
  { field: 'isRulerEnabled', label: 'Линейка' },
  { field: 'isInteractiveTopViewEnabled', label: 'Интерактивный верхний вид' },
  { field: 'isTopViewAsMiniPlanEnabled', label: 'Верхний вид как мини-план' },
  { field: 'hideOptionalFurniture', label: 'Скрывать необязательную мебель' },
  { field: 'isTransparentTopViewEnabled', label: 'Прозрачный верхний вид' },
  { field: 'isWebpEnabled', label: 'WEBP' },
  { field: 'applyMiddleCutInTopView', label: 'Срединный срез (при съемке сверху)' },
];

const resetData = () => {
  Object.assign(modal, getDefaultModalData());
};

watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      resetData();
    }
  },
);

const closeModal = () => {
  resetData();
  emit('close');
};

const onModalSubmit = async () => {
  if (isSubmitting.value) return;

  modal.unrealRtxPresetTop = modal.unrealRtxPreset;

  const requestData = { ...modal };
  if (modal.renderServer) {
    requestData.overrideNodeLabel = modal.renderServer;
    delete requestData.renderServer;
  }

  isSubmitting.value = true;
  try {
    const response = await launchWithConfirm(
      (force, choice) => api.runUnrealGeneration(props.plan.id, { ...requestData, ...choice }, force),
    );
    if (!response) return;

    closeModal();
    location.reload();
  } finally {
    isSubmitting.value = false;
  }
};

</script>
